import React, { useMemo, useState } from "react";
import PulseSelect from "./Select/PulseSelect";
import useChartEditorStyles from "./themes";

import _ from "lodash";
import { SearchResult } from "qpr-base/apihooks/useSearch";
import { If } from "react-if";
import { QueryKey, UseQueryOptions } from "react-query";
import ElementSelect from "./ElementSelect";
import PeriodSelector from "./PeriodSelector/PeriodSelector";

import IconButton from "@material-ui/core/IconButton";
import BarChartIcon from "@material-ui/icons/BarChart";
import DonutLargeIcon from "@material-ui/icons/DonutLarge";
import PieChartIcon from "@material-ui/icons/PieChart";
import TimelineIcon from "@material-ui/icons/Timeline";
import { useTranslation } from "react-i18next";
import { TChartComponentData, TChartOption, seriesOptions } from "./types";

import {
  TDashboardEditComponent,
  TDashboardEditComponentProps,
} from "MyDashboards/components/ComponentRegistry/types";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import Query from "qpr-base/querybuilder/Query";
import Loader from "qpr-base/ui/components/Loader";
import { useQuery } from "react-query";
import { ReactComponent as BarLineComboIcon } from "../../../../assets/combo-chart.svg";

type ElementType = "kpi" | "project";

const allChartOptions = [
  { value: "column", title: "Column", Icon: BarChartIcon },
  {
    value: "bar",
    title: "Bar",
    Icon: () => <BarChartIcon style={{ transform: "rotate(90deg)" }} />,
  },
  { value: "column_line", title: "ColumnLine", Icon: BarLineComboIcon },
  { value: "donut", title: "Donut", Icon: DonutLargeIcon },
  { value: "line", title: "Line", Icon: TimelineIcon },
  { value: "pie", title: "Pie", Icon: PieChartIcon },
];

const ChartEditor: TDashboardEditComponent = (
  props: TDashboardEditComponentProps
) => {
  const classes = useChartEditorStyles();
  const [searchText, setSearchText] = useState<string>("");
  const { el } = props;
  const compData = React.useMemo<TChartComponentData | undefined>(() => {
    if (el.component) {
      return el.component.data as TChartComponentData;
    }
    return undefined;
  }, [el]);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { value } = e.currentTarget;
    _.debounce(() => setSearchText(value), 3000);
  };
  const searchOptions: Omit<
    UseQueryOptions<
      { data: SearchResult[] },
      unknown,
      { data: SearchResult[] },
      QueryKey
    >,
    "queryKey" | "queryFn"
  > = {
    enabled: false,
  };
  const [periodRange, setPeriodRange] = useState<string[]>(
    () => compData?.selectedPeriods || []
  );

  const [selectedSeries, setSelectedSeries] = useState<
    TChartOption | undefined
  >(() => compData?.selectedSeries);
  const [selectedKPI, setSelectedKPI] = React.useState<
    TChartOption | undefined
  >(() => compData?.selectedElement);
  const [selectedChart, setSelectedChart] = React.useState<string | undefined>(
    () => compData?.selectedChart
  );

  const { t } = useTranslation();
  const { rtl } = useAppContext();
  const OPTIONS = useMemo(() => {
    return [{ label: t("KPI"), value: "kpi" }];
  }, [rtl]);
  const availableChartOptions = React.useMemo(() => {
    const multiPeriodOptions = allChartOptions.filter(
      (option) => option.value !== "pie" && option.value !== "donut"
    );
    const isMultiPeriod =
      periodRange.length > 1 && periodRange[0] !== periodRange[1];
    if (isMultiPeriod) {
      if (selectedSeries?.value === "kpi_act_tar") return multiPeriodOptions;
      return multiPeriodOptions.filter(
        (option) => option.value !== "column_line"
      );
    }

    return allChartOptions.filter((option) => option.value !== "column_line");
  }, [selectedSeries, periodRange]);

  React.useEffect(() => {
    const currentSelectedChart = availableChartOptions.find(
      (c) => c.value === selectedChart
    );
    if (currentSelectedChart) return;
    setSelectedChart(availableChartOptions[0].value);
  }, [availableChartOptions, selectedChart]);
  const selectedChartTitle = React.useMemo(() => {
    return allChartOptions.filter((c) => c.value === selectedChart).pop()
      ?.title;
  }, [selectedChart, availableChartOptions]);
  React.useEffect(() => {
    props.onChange({
      component: {
        ...(el.component || {}),
        tag: "COMPONENT",
        width: 500,
        height: 300,
        component: "chart",
        data: {
          selectedSeries,
          selectedElement: selectedKPI,
          selectedChart,
          selectedPeriods: periodRange,
        },
      },
    });
  }, [selectedSeries, selectedKPI, selectedChart, periodRange]);

  const translatedSeriesOptions = React.useMemo(() => {
    return seriesOptions.map((option) => ({
      label: t(`${option.label}`),
      value: option.value,
    }));
  }, [rtl]);

  const getElements = async (
    modelId: string,
    type: ElementType
  ): Promise<
    {
      id: string;
      name: string;
      startdate: string;
      enddate: string;
      periodlevel: string;
      "scorecard.name"?: string;
    }[]
  > => {
    if (type === "project") {
      return Query(modelId)
        .PROJECT({
          criteria: {
            hasreferencescorecard: "0",
          },
        })
        .attributes("name")
        .fetchValues();
    }
    return Query(modelId)
      .KPI({
        criteria: 'hasreferencescorecard="0" AND scorecard.symbol <> "SCHEMA"',
      })
      .attributes("name", "scorecard.name", "periodlevel")
      .fetchValues();
  };

  const useElements = (modelId: string, type: ElementType) => {
    const ctx = useAppContext();
    return useQuery(
      `kpis-${modelId}`,
      async () => {
        return getElements(ctx.fullModelId || "", type);
      },
      { staleTime: 10 * 60 * 1000, cacheTime: 10 * 60 * 1000 }
    );
  };

  const ctx = useAppContext();
  const { data: elements, isLoading: isElementsLoading } = useElements(
    ctx.modelId,
    "kpi"
  );

  if (isElementsLoading) return <Loader />;
  return (
    <div className={classes.container}>
      <div className={classes.contentContainer}>
        <div className={classes.whiteContainer}>
          <PulseSelect
            name="name"
            placeholder={`${t("myStories.Select")}...`}
            isSearchable={false}
            label={t("myStories.Select Element Type")}
            onChange={handleChange}
            options={OPTIONS}
            value={OPTIONS[0]}
          />
          <ElementSelect
            name="selectedKPI"
            elements={elements || []}
            placeholder={`${t("myStories.Select")}...`}
            label={`${t("myStories.Select KPI")}`}
            onElementSelected={(e: any) => {
              setSelectedKPI(e);
            }}
            isSearchable
            value={selectedKPI}
          />
          <PulseSelect
            name="selectedSeries"
            isSearchable={false}
            placeholder={`${t("myStories.Select")}...`}
            label={t("myStories.Select Series")}
            onChange={setSelectedSeries}
            options={translatedSeriesOptions}
            value={selectedSeries}
          />
          <p style={{ alignSelf: "start" }}>
            {t("myStories.Period")}{" "}
            {(periodRange[1] && periodRange.filter((p) => p).join(" - ")) ||
              periodRange[0]}
          </p>
          <PeriodSelector
            elementId={selectedKPI?.value}
            periodLevel={
              elements?.find((e) => e.id === selectedKPI?.value)?.periodlevel
            }
            from={periodRange[0]}
            to={periodRange[1] || ""}
            onChange={(start, end) => {
              /** TODO handle period change */
              if (start && end) {
                setPeriodRange([start, end]);
              }
              if (start && !end) {
                setPeriodRange([start]);
              }
            }}
          />

          <If condition={!!selectedSeries}>
            <>
              <p style={{ alignSelf: "start" }}>
                {t("myStories.Chart Type")} ({t(selectedChartTitle || "")})
              </p>
              <div className={classes.chartIconsContainer}>
                {availableChartOptions.map((item) => {
                  return (
                    <IconButton
                      title={item.title}
                      aria-label={item.title}
                      className={
                        item.value === selectedChart
                          ? classes.selectedChart
                          : undefined
                      }
                      style={{
                        border:
                          item.value === selectedChart
                            ? "0px solid gray"
                            : "1px solid rgba(0, 0, 0, 0.22)",
                      }}
                      onClick={() => {
                        setSelectedChart(item.value);
                      }}
                    >
                      <item.Icon />
                    </IconButton>
                  );
                })}
              </div>
            </>
          </If>
        </div>
      </div>
    </div>
  );
};
export default ChartEditor;
